import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';
import { AiChatService } from '@app/core/services/ai-chat.service';
import { AiToolCallItem } from '@app/core/services/ai-chat.types';
import { TT_VIEW_NAVIGATOR, TTViewNavigator } from '@app/modal/state-modal/state-modal.component';
import { sanitizeAiChatPromptHtml } from '@app/core/services/ai-chat.utils';
import hljs from 'highlight.js/lib/core';
import sql from 'highlight.js/lib/languages/sql';
import { format as formatSql } from 'sql-formatter';

hljs.registerLanguage('sql', sql);

interface AiToolCallMessageGroup {
    aiMessageKeyno: `${number}`;
    promptHtml: string;
    toolCalls: AiToolCallItem[];
}

@Component({
    selector: 'tt-ai-chat-tool-calls-dialog',
    templateUrl: './ai-chat-tool-calls-dialog.component.html',
    styleUrls: ['./ai-chat-tool-calls-dialog.component.css'],
    standalone: false,
})
export class AiChatToolCallsDialogComponent implements OnInit, OnDestroy {
    public toolCalls: AiToolCallItem[] = [];

    public messageGroups: AiToolCallMessageGroup[] = [];

    public loading = true;

    public errorMessage = '';

    public copiedToolCallKeyno?: `${number}`;

    private readonly highlightedSqlBySource = new Map<string, string>();

    private readonly formattedSqlBySource = new Map<string, string>();

    private readonly sanitizedPromptHtmlBySource = new Map<string, string>();

    private copiedTooltipResetTimer?: number;

    constructor(
        private aiChat: AiChatService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {}

    public async ngOnInit(): Promise<void> {
        this.navigator?.setSize?.({ width: 'min(95vw, 100rem)', height: '98vh' });
        this.navigator?.setHeading?.('AI tool calls');
        const aiChatKeyno = this.getAiChatKeyno();

        if (!aiChatKeyno) {
            this.errorMessage = 'A valid AI chat is required.';
            this.loading = false;
            return;
        }

        try {
            this.toolCalls = (await this.aiChat.getAiToolCalls({ ai_chat_keyno: aiChatKeyno })) ?? [];
            this.messageGroups = this.groupToolCallsByMessage(this.toolCalls);
        } catch {
            this.errorMessage = 'The AI tool calls could not be loaded.';
        } finally {
            this.loading = false;
        }
    }

    public ngOnDestroy(): void {
        if (this.copiedTooltipResetTimer !== undefined) window.clearTimeout(this.copiedTooltipResetTimer);
    }

    /**
     * Returns Highlight.js SQL markup. Highlight.js escapes source text and Angular retains its
     * normal `[innerHTML]` sanitization, so API SQL is never trusted as arbitrary HTML.
     */
    public getHighlightedSql(sqlCode: string): string {
        const source = sqlCode ?? '';
        const cachedHtml = this.highlightedSqlBySource.get(source);

        if (cachedHtml !== undefined) return cachedHtml;

        const highlightedHtml = hljs.highlight(this.getFormattedSql(source), { language: 'sql', ignoreIllegals: true }).value;
        this.highlightedSqlBySource.set(source, highlightedHtml);
        return highlightedHtml;
    }

    /** Returns the plain formatted SQL used both for display and clipboard copying. */
    public getFormattedSql(sqlCode: string): string {
        const source = sqlCode ?? '';
        const cachedSql = this.formattedSqlBySource.get(source);

        if (cachedSql !== undefined) return cachedSql;

        const formattedSql = this.formatSql(source);
        this.formattedSqlBySource.set(source, formattedSql);
        return formattedSql;
    }

    /** Copies formatted SQL without the Highlight.js HTML markup and confirms success in the button tooltip. */
    public async copyFormattedSql(sqlCode: string, aiToolCallKeyno: `${number}`, tooltip: MatTooltip): Promise<void> {
        const formattedSql = this.getFormattedSql(sqlCode);
        let copied = false;

        try {
            await navigator.clipboard.writeText(formattedSql);
            copied = true;
        } catch {
            copied = this.copyTextWithFallback(formattedSql);
        }

        if (!copied) return;

        this.copiedToolCallKeyno = aiToolCallKeyno;
        tooltip.show(0);

        if (this.copiedTooltipResetTimer !== undefined) window.clearTimeout(this.copiedTooltipResetTimer);
        this.copiedTooltipResetTimer = window.setTimeout(() => {
            this.copiedToolCallKeyno = undefined;
        }, 1_500);
    }

    public getCopyTooltip(aiToolCallKeyno: `${number}`): string {
        return this.copiedToolCallKeyno === aiToolCallKeyno ? 'Copied' : 'Copy to clipboard';
    }

    /** Returns sanitized prompt HTML; Angular applies its normal `[innerHTML]` sanitization as well. */
    public getSanitizedPromptHtml(promptHtml: string): string {
        const source = promptHtml ?? '';
        const cachedHtml = this.sanitizedPromptHtmlBySource.get(source);

        if (cachedHtml) return cachedHtml;

        const sanitizedHtml = sanitizeAiChatPromptHtml(source);
        this.sanitizedPromptHtmlBySource.set(source, sanitizedHtml);
        return sanitizedHtml;
    }

    public trackMessageGroup(_: number, group: AiToolCallMessageGroup): string {
        return group.aiMessageKeyno;
    }

    public trackToolCall(_: number, toolCall: AiToolCallItem): string {
        return toolCall.ai_tool_call_keyno;
    }

    /** Uses a general SQL dialect and preserves the original query if formatting cannot parse it. */
    private formatSql(sqlCode: string): string {
        try {
            return formatSql(sqlCode, {
                language: 'sql',
                keywordCase: 'upper',
                tabWidth: 4,
                expressionWidth: 88,
            });
        } catch {
            return sqlCode;
        }
    }

    private groupToolCallsByMessage(toolCalls: AiToolCallItem[]): AiToolCallMessageGroup[] {
        const groupsByMessageKeyno = new Map<string, AiToolCallMessageGroup>();

        for (const toolCall of toolCalls) {
            let group = groupsByMessageKeyno.get(toolCall.ai_message_keyno);

            if (!group) {
                group = {
                    aiMessageKeyno: toolCall.ai_message_keyno,
                    promptHtml: toolCall.prompt,
                    toolCalls: [],
                };
                groupsByMessageKeyno.set(toolCall.ai_message_keyno, group);
            }

            group.toolCalls.push(toolCall);
        }

        return [...groupsByMessageKeyno.values()];
    }

    private getAiChatKeyno(): `${number}` | undefined {
        const value = this.navigator?.params['ai_chat_keyno'];
        return typeof value === 'string' && /^\d+$/.test(value) ? (value as `${number}`) : undefined;
    }

    private copyTextWithFallback(value: string): boolean {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        textarea.remove();
        return copied;
    }
}
